/**
 * Diálogo para descartar una tarea con su motivo (ADR-0017). Modal accesible (carcasa común
 * `componentes/Dialogo`): foco al primer motivo al abrir, Tab atrapado dentro y Esc o «Cancelar» lo
 * cierran (quien lo abre devuelve el estado anterior y el foco).
 *
 * Valida con el mismo `descarteSchema` del contrato que usa la API: «Duplicada» pide la tarea original
 * («CDT-12» o «12»); los demás motivos piden un detalle. Si la API rechaza (p. ej. la original no existe),
 * el mensaje se muestra aquí mismo y el diálogo sigue abierto.
 */
import { useId, useRef, useState } from 'react';
import {
  ETIQUETA_MOTIVO_DESCARTE,
  MOTIVOS_DESCARTE,
  descarteSchema,
  formatearNumero,
  leerNumeroTarea,
  type DescarteEntrada,
  type MotivoDescarte,
} from '@cdtec-backlog/contrato';
import { Dialogo } from '../../componentes/Dialogo';
import { Giro } from '../../componentes/Esqueleto';
import './DialogoDescartar.css';

interface Props {
  numero: number;
  enviando: boolean;
  /** Mensaje de la API si rechazó el descarte. */
  errorApi?: string | null;
  onCancelar: () => void;
  onConfirmar: (descarte: DescarteEntrada) => void;
}

type Errores = Partial<Record<'detalle' | 'duplicadaDe', string>>;

export function DialogoDescartar({ numero, enviando, errorApi, onCancelar, onConfirmar }: Props) {
  const [motivo, setMotivo] = useState<MotivoDescarte>('NO_POR_AHORA');
  const [original, setOriginal] = useState('');
  const [detalle, setDetalle] = useState('');
  const [errores, setErrores] = useState<Errores>({});
  const primerMotivo = useRef<HTMLInputElement>(null);
  const campoOriginal = useRef<HTMLInputElement>(null);
  const campoDetalle = useRef<HTMLTextAreaElement>(null);
  const id = useId();

  const elegir = (nuevo: MotivoDescarte) => {
    setMotivo(nuevo);
    setErrores({});
  };

  const confirmar = () => {
    const resultado = descarteSchema.safeParse({
      motivo,
      detalle,
      duplicadaDe: motivo === 'DUPLICADA' ? leerNumeroTarea(original) : null,
    });
    if (!resultado.success) {
      const nuevos: Errores = {};
      for (const problema of resultado.error.issues) {
        const campo = problema.path[0];
        if (campo === 'detalle' || campo === 'duplicadaDe') nuevos[campo] ??= problema.message;
      }
      setErrores(nuevos);
      if (nuevos.duplicadaDe) campoOriginal.current?.focus();
      else campoDetalle.current?.focus();
      return;
    }
    if (resultado.data.duplicadaDe === numero) {
      setErrores({ duplicadaDe: 'Una tarea no puede ser duplicada de sí misma.' });
      campoOriginal.current?.focus();
      return;
    }
    setErrores({});
    onConfirmar(resultado.data);
  };

  const esDuplicada = motivo === 'DUPLICADA';

  return (
    <Dialogo
      etiquetadoPor={`${id}-titulo`}
      descritoPor={`${id}-ayuda`}
      focoInicial={primerMotivo}
      onCancelar={onCancelar}
    >
      <h2 id={`${id}-titulo`} className="dialogo__titulo">
        Descartar {formatearNumero(numero)}
      </h2>
      <p id={`${id}-ayuda`} className="dialogo__ayuda">
        Sale del tablero y queda en «Cerradas» con su motivo. Se puede reabrir cuando quieras.
      </p>

      <fieldset className="motivos">
        <legend className="solo-lectores">Motivo</legend>
        {MOTIVOS_DESCARTE.map((valor, indice) => (
          <label key={valor} className="motivos__opcion">
            <input
              ref={indice === 0 ? primerMotivo : undefined}
              type="radio"
              name={`${id}-motivo`}
              value={valor}
              checked={motivo === valor}
              onChange={() => elegir(valor)}
            />
            {valor === 'DUPLICADA' ? 'Duplicada de' : ETIQUETA_MOTIVO_DESCARTE[valor]}
            {valor === 'DUPLICADA' && (
              <input
                ref={campoOriginal}
                className="motivos__original"
                type="text"
                inputMode="text"
                placeholder="CDT-12"
                aria-label="Tarea original"
                aria-invalid={errores.duplicadaDe ? true : undefined}
                aria-describedby={errores.duplicadaDe ? `${id}-error-original` : undefined}
                value={original}
                // Escribir la original elige «Duplicada» (sin borrar el error si ya estaba elegida).
                onFocus={() => motivo !== 'DUPLICADA' && elegir('DUPLICADA')}
                onChange={(e) => {
                  setOriginal(e.target.value);
                  setErrores({});
                }}
              />
            )}
          </label>
        ))}
      </fieldset>
      {errores.duplicadaDe && (
        <p id={`${id}-error-original`} className="dialogo__error" role="alert">
          {errores.duplicadaDe}
        </p>
      )}

      <div className="dialogo__campo">
        <label htmlFor={`${id}-detalle`}>Detalle{esDuplicada ? ' (opcional)' : ''}</label>
        <textarea
          ref={campoDetalle}
          id={`${id}-detalle`}
          rows={3}
          maxLength={1000}
          placeholder={
            esDuplicada ? 'Algo más que agregar (opcional)' : 'Por qué se descarta (obligatorio)'
          }
          aria-invalid={errores.detalle ? true : undefined}
          aria-describedby={errores.detalle ? `${id}-error-detalle` : undefined}
          value={detalle}
          onChange={(e) => {
            setDetalle(e.target.value);
            setErrores({});
          }}
        />
        {errores.detalle && (
          <p id={`${id}-error-detalle`} className="dialogo__error" role="alert">
            {errores.detalle}
          </p>
        )}
      </div>

      {errorApi && (
        <p className="dialogo__error" role="alert">
          {errorApi}
        </p>
      )}

      <div className="dialogo__acciones">
        <button type="button" className="boton" onClick={onCancelar}>
          Cancelar
        </button>
        <button
          type="button"
          className="boton boton--peligro-lleno boton--con-giro"
          disabled={enviando}
          onClick={confirmar}
        >
          {enviando && <Giro />}
          {enviando ? 'Descartando…' : 'Descartar tarea'}
        </button>
      </div>
    </Dialogo>
  );
}
